import { useMemo, useState } from "react";
import API from "../config/api.js";

export default function DraftCard({ draft, onRemove, onQueued }) {
  const [isRemoving, setIsRemoving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const parsedItems = useMemo(() => {
    if (!draft?.items) return [];

    try {
      const parsed = JSON.parse(draft.items);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.error("Erro ao fazer parse dos items do draft:", error);
      return [];
    }
  }, [draft]);

  const hasError =
    draft?.facebookautomationstatus === "error" ||
    draft?.instagramautomationstatus === "error" ||
    draft?.linkedinautomationstatus === "error";

  const formatMd5LikeId = (value) => value;

  const draftId = formatMd5LikeId(draft.iddraft);

  const getItemThumbnail = (item) => {
    if (!item) return "/assets/img/350x350.png";

    if (item.type === "image" || item.type === "story") {
      return `${API.baseUrl}/upload/image/${item.id}/image/350x350.jpg`;
    }

    return `${API.baseUrl}/upload/${item.type}/${item.id}/image/original.jpg`;
  };

  const handleAddToQueue = async (e) => {
    e.stopPropagation();

    try {
      setIsSubmitting(true);

      const preloader = document.getElementById("preloader");
      const preloaderTitle = document.querySelector("#preloader .title");

      if (preloader) {
        preloader.style.display = "block";
      }

      if (preloaderTitle) {
        preloaderTitle.innerHTML = "Adding To Queue...";
      }

      const formData = new FormData();
      formData.append("iddraft", draft.iddraft);

      const res = await fetch(`${API.drafts}?nextSpotDate`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      const response = await res.json();

      if (response) {
        setIsRemoving(true);

        setTimeout(() => {
          onQueued?.(draft.iddraft);
        }, 800);

        window._alert?.("success", "✅ Post Scheduled");
      } else {
        window._alert?.("danger", "Error: drafts: draft_add_to_queue");
      }
    } catch (error) {
      console.error("Error: drafts: draft_add_to_queue", error);
      window._alert?.("danger", "Error: drafts: draft_add_to_queue");
    } finally {
      const preloader = document.getElementById("preloader");
      const preloaderTitle = document.querySelector("#preloader .title");

      if (preloader) {
        preloader.style.display = "none";
      }

      if (preloaderTitle) {
        preloaderTitle.innerHTML = "";
      }

      setIsSubmitting(false);
    }
  };

  const handleRemove = async (e) => {
    e.stopPropagation();

    const confirmation = window.confirm(
      "Are you sure you want to remove the item?"
    );

    if (!confirmation) return;

    try {
      setIsSubmitting(true);

      const formData = new FormData();
      formData.append("iddraft", draft.iddraft);

      const res = await fetch(`${API.drafts}?remove`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      const response = await res.json();

      if (response === true) {
        onRemove?.(draft.iddraft);
      } else {
        window.alert("Error: item not removed.");
      }
    } catch (error) {
      console.error("Error: drafts: remove", error);
      window.alert("Error: drafts: remove");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (e, duplicate = false) => {
    e.stopPropagation();
    window._draft_edit?.(draftId, duplicate, true);
  };

  const handleCopyToClipboard = (e) => {
    e.stopPropagation();
    window._copy_to_clipboard?.(draft.content || "");
  };

  return (
    <div
      className={`card h-100 rounded-4 animate__animated ${
        isRemoving ? "animate__fadeOut" : "animate__fadeIn animate__slow"
      } draft-${draftId}`}
      id={`draft-${draftId}`}
      data-iddraft={draftId}
    >
      <div className="position-relative p-2">
        {parsedItems.length ? (
          <div
            id={`draft-carousel-${draftId}`}
            className="carousel slide"
            data-bs-touch="false"
            data-bs-interval="false"
          >
            <div className="carousel-inner">
              {parsedItems.map((item, index) => {
                const thumbnail = getItemThumbnail(item);

                return (
                  <div
                    key={`${draftId}-${index}`}
                    className={`carousel-item ${index === 0 ? "active" : ""}`}
                  >
                    {item.type === "image" || item.type === "story" ? (
                      <img
                        src={thumbnail}
                        onError={(e) => {
                          e.currentTarget.src = "/assets/img/350x350.png";
                        }}
                        alt=""
                        className={`w-100 h-100 rounded-3 bg-light${
                          hasError ? " grayscale" : ""
                        }`}
                        width="350"
                        height="350"
                        loading="lazy"
                      />
                    ) : (
                      <div
                        className="position-relative"
                      >
                        <img
                          src="/assets/img/350x350-transparent.png"
                          style={{
                            background: `url('${thumbnail}')`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                          }}
                          alt=""
                          className={`w-100 h-100 rounded-3 bg-light${
                            hasError ? " grayscale" : ""
                          }`}
                          width="350"
                          height="350"
                          loading="lazy"
                        />
                        <div className="position-absolute top-50 start-50 translate-middle w-100 text-center">
                          <img
                            src="/assets/img/icon/play.png"
                            className="w-auto mx-auto"
                            alt=""
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {parsedItems.length > 1 && (
              <>
                <div className="carousel-indicators">
                  {parsedItems.map((item, index) => (
                    <button
                      key={`${draftId}-indicator-${index}`}
                      type="button"
                      data-bs-target={`#draft-carousel-${draftId}`}
                      data-bs-slide-to={index}
                      className={index === 0 ? "active" : ""}
                    ></button>
                  ))}
                </div>

                <button
                  className="carousel-control-prev"
                  type="button"
                  data-bs-target={`#draft-carousel-${draftId}`}
                  data-bs-slide="prev"
                >
                  <span
                    className="carousel-control-prev-icon"
                    aria-hidden="true"
                  ></span>
                  <span className="visually-hidden">Previous</span>
                </button>

                <button
                  className="carousel-control-next"
                  type="button"
                  data-bs-target={`#draft-carousel-${draftId}`}
                  data-bs-slide="next"
                >
                  <span
                    className="carousel-control-next-icon"
                    aria-hidden="true"
                  ></span>
                  <span className="visually-hidden">Next</span>
                </button>
              </>
            )}
          </div>
        ) : (
          <img
            src="/assets/img/350x350.png"
            alt=""
            className="w-100 bg-light rounded-3"
            width="350"
            height="350"
            loading="lazy"
          />
        )}

        <div className="position-absolute top-0 end-0">
          <div
            className="bg-white text-dark rounded text-center m-3 lh-1 p-2"
            style={{ width: "45px", height: "41px", fontSize: "12px" }}
          >
            <strong>{new Date(draft.date).toLocaleDateString("en-US", { day: "2-digit" })}</strong>
            <br />
            {new Date(draft.date).toLocaleDateString("en-US", { month: "short" })}
          </div>
        </div>

        <div className="p-3 position-absolute bottom-0 start-0 text-center text-white small">
          {draft.type ? draft.type.charAt(0).toUpperCase() + draft.type.slice(1) : ""}
        </div>

        <div
          className="p-1 position-absolute top-0 start-0 text-center text-white"
          data-bs-toggle="tooltip"
          data-bs-placement="top"
          data-bs-title="Add To Queue"
          style={{ zIndex: 1000 }}
        >
          <button
            className="btn text-white"
            onClick={handleAddToQueue}
            style={{ fontSize: "21px" }}
            disabled={isSubmitting}
          >
            <img
              src="/assets/img/icon/check-circle.png"
              height="24"
              width="24"
              alt=""
            />
          </button>
        </div>
      </div>

      <div className="card-body">
        <p
          className="card-text draft-content"
          dangerouslySetInnerHTML={{
            __html: (draft.content || "").replace(/\n/g, "<br>"),
          }}
        ></p>
      </div>

      <div className="card-footer bg-transparent p-3">
           <div className="row g-3 align-items-center">
            <div className="col-4 action-buttons d-block">
              {["image", "story", "reel", "video", "carousel"].includes(draft.type) && (
                <button
                  type="button"
                  className="btn"
                  onClick={(e) => handleEdit(e, false)}
                >
                  <i className="fi fi-rr-add fs-5"></i>
                </button>
              )}
            </div>

            <div className="col-8 action-buttons text-end">
              {["image", "story", "reel", "video", "carousel"].includes(draft.type) && (
                <div className="dropup d-inline-block z-3">
                  <button
                    className="btn"
                    type="button"
                    id={`dropdownMenuDraft-${draft.iddraft}`}
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <i className="fi fi-rr-menu-dots"></i>
                  </button>

                  <ul
                    className="dropdown-menu dropdown-menu-end"
                    aria-labelledby={`dropdownMenuDraft-${draft.iddraft}`}
                  >
                    <li>
                      <button
                        className="dropdown-item"
                        onClick={(e) => handleEdit(e, true)}
                      >
                        <i className="fi fi-rr-window-restore me-2"></i>
                        Duplicate Into Post Builder
                      </button>
                    </li>

                    <li>
                      <button
                        className="dropdown-item"
                        onClick={handleCopyToClipboard}
                      >
                        <i className="fi fi-rr-duplicate me-2"></i>
                        Copy To Clipboard
                      </button>
                    </li>

                    <li>
                      <button
                        className="dropdown-item"
                        onClick={handleRemove}
                      >
                        <i className="fi fi-rr-trash me-2"></i>
                        Delete
                      </button>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>
      </div>
    </div>
  );
}